/**
 * DocumentVersionsPage - Page for viewing document version history
 *
 * Accessible via /admin/records/:id/versions route.
 * Shows a sidebar with version list and content area for selected version.
 */
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, FileText, Loader2 } from "lucide-react";
import { VersionHistory } from "../../components/admin/versions/VersionHistory";
import { useRecordsApi } from "../../lib/api/records";

export function DocumentVersionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getRecord } = useRecordsApi();

  const documentId = id ? parseInt(id, 10) : null;

  // Fetch document info for header
  const {
    data: document,
    isLoading: isLoadingDocument,
    error: documentError,
  } = useQuery({
    queryKey: ["document", documentId],
    queryFn: () => getRecord(id!),
    enabled: !!id,
  });

  if (!documentId || isNaN(documentId)) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-6">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600 font-medium">Invalid document ID</p>
          <Link
            to="/admin/records"
            className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-800"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Records
          </Link>
        </div>
      </div>
    );
  }

  if (documentError) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center p-6">
          <p className="text-red-600 font-medium">Failed to load document</p>
          <p className="text-gray-500 text-sm mt-1">
            {documentError instanceof Error ? documentError.message : "Unknown error"}
          </p>
          <Link
            to="/admin/records"
            className="mt-4 inline-flex items-center text-indigo-600 hover:text-indigo-800"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to Records
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/records")}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              title="Back to Records"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div>
              {isLoadingDocument ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                  <span className="text-gray-400">Loading...</span>
                </div>
              ) : (
                <>
                  <h1 className="text-xl font-semibold text-gray-900">
                    Version History
                  </h1>
                  <p className="text-sm text-gray-500 mt-0.5 truncate max-w-xl">
                    {document?.title || `Document #${documentId}`}
                  </p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to={`/admin/records/${id}/edit`}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              Edit Current Version
            </Link>
          </div>
        </div>
      </div>

      {/* Version History Container */}
      <div className="flex-1 overflow-hidden">
        <VersionHistory
          documentId={documentId}
          documentTitle={document?.title}
        />
      </div>
    </div>
  );
}
